// @ts-check
import { observeImage } from '../../platform/assets.js';
import { prepareRunSource } from './run-cycle.js';
import { POSES, LEFT_POSES } from './directional.js';

const IDLE_DELAY = 2;
/** @param {import('../../game/player.js').Position} player */
const isStill = player => player.grounded && Math.abs(player.vx) <= 10 && !player.crouching && !player.sliding && !player.impact && !player.recoil && !player.boost && player.pound === 'none';

/** Supplemental, pre-authored poses. Movement and transfers keep their own artwork.
 * CSS steps animate the small portraits even while simulation is stopped.
 * @param {import('../../ui/dom.js').GameElements} view @param {()=>void} onReady
 */
export function createReactions(view, onReady) {
  const root = view.root;
  const source = root.createElement('img'); source.id = 'qp-reactions-source'; source.hidden = true; source.alt = '';
  source.src = new URL('qp-reactions.webp', view.sources.original.src).href;
  root.body.append(source);
  const sleepSource = root.createElement('img'); sleepSource.id = 'qp-sleep-source'; sleepSource.hidden = true; sleepSource.alt = '';
  sleepSource.src = new URL('qp-sleep-source.png', view.sources.original.src).href; root.body.append(sleepSource);
  let loaded = false, disposed = false, settling = true, idleAge = 0, welcomeId = 0, idleId = 0;
  /** @type {Array<()=>void>} */ const cleanups = [];
  /** @param {HTMLElement} host @param {string} id */
  function sprite(host, id) {
    let node = root.createElement('span'); node.id = id; node.className = 'qp-reaction'; node.setAttribute('aria-hidden', 'true'); node.hidden = true; host.append(node);
    /** @type {unknown} */ let previousKey;
    cleanups.push(() => node.remove());
    return {
      get element() { return node; },
      /** Start a new finite reaction without timers, layout reads, or retained nodes.
       * @param {'idle'|'welcome'|'celebrate'|'disabled'} reaction @param {unknown} key @param {boolean} visible @param {boolean} paused @param {boolean} reduced
       */
      render(reaction, key, visible, paused, reduced) {
        if (previousKey !== key) {
          previousKey = key;
          const fresh = /** @type {HTMLSpanElement} */ (node.cloneNode(false));
          // Move cached canvases: cloning a canvas discards its painted bitmap.
          fresh.append(...node.childNodes); node.replaceWith(fresh); node = fresh;
        }
        node.hidden = !loaded || !visible;
        if (node.hidden) return false;
        node.dataset.reaction = reaction; node.dataset.paused = String(paused); node.dataset.reduced = String(reduced);
        node.style.backgroundImage = `url("${source.src}")`;
        return true;
      },
    };
  }
  /** Keep heading text in the accessibility tree and restore its position on disposal.
   * @param {HTMLElement} title @param {string} id
   */
  function portrait(title, id) {
    const heading = root.createElement('div'); heading.className = 'reaction-heading'; title.before(heading);
    const art = sprite(heading, id); heading.append(title);
    cleanups.push(() => { heading.before(title); heading.remove(); });
    return art;
  }
  const idle = sprite(view.qp, 'qp-idle');
  const rest = root.createElement('span'); rest.className = 'qp-idle-rest'; idle.element.append(rest);
  const sleepSignal = root.createElement('span'); sleepSignal.className = 'qp-sleep-signal'; sleepSignal.textContent = 'z z Z'; rest.append(sleepSignal);
  const releaseSleep = observeImage(sleepSource, () => {
    if (!sleepSource.complete || sleepSource.naturalWidth !== 1536 || sleepSource.naturalHeight !== 1024) return;
    const clean = prepareRunSource(sleepSource);
    if (!(clean instanceof HTMLCanvasElement)) return;
    // Measured whole poses share head scale and a planted baseline, not a grid crop.
    const cells = [
      { x: 190, y: 30, width: 420, height: 530, anchor: 395, floor: 550 },
      { x: 945, y: 65, width: 440, height: 500, anchor: 1160, floor: 550 },
      { x: 50, y: 620, width: 680, height: 340, anchor: 390, floor: 944 },
      { x: 810, y: 650, width: 690, height: 310, anchor: 1150, floor: 944 },
    ];
    const canvases = cells.map((cell, index) => {
      const canvas = root.createElement('canvas'); canvas.width = 800; canvas.height = 600;
      canvas.className = 'qp-rest-cel'; canvas.dataset.cel = String(index);
      const context = canvas.getContext('2d');
      context?.drawImage(clean, cell.x, cell.y, cell.width, cell.height, 400 + cell.x - cell.anchor, 600 + cell.y - cell.floor, cell.width, cell.height);
      return { canvas, ready: !!context };
    });
    clean.width = clean.height = 0;
    if (canvases.some(cel => !cel.ready)) return;
    rest.prepend(...canvases.map(cel => cel.canvas)); idle.element.dataset.sleepReady = 'true';
    if (!disposed) onReady();
  });
  const welcome = portrait(view.flowTitle, 'qp-welcome');
  // The playable image is already required. Reuse its registered standing cel
  // while the optional greeting atlas loads, so readiness always includes QP.
  const standin = root.createElement('canvas'); standin.className = 'entrance-standin';
  standin.width = 192; standin.height = 208; standin.setAttribute('aria-hidden', 'true'); standin.hidden = true;
  view.flow.append(standin);
  const presence = root.createElement('img'); presence.id = 'qp-presence-source'; presence.hidden = true; presence.alt = '';
  presence.src = new URL('qp-presence-wave.png', view.sources.original.src).href; root.body.append(presence);
  const releaseStandin = observeImage(view.image, () => {
    if (standin.dataset.presence === 'ready') return;
    const clean = prepareRunSource(view.image), context = standin.getContext('2d');
    const cel = view.image.src.includes('qp-move-left') ? LEFT_POSES[0] : POSES[0];
    if (context) {
      const scale = 196 / cel.height;
      context.drawImage(clean, cel.x, cel.y, cel.width, cel.height, (192 - cel.width * scale) / 2, 8, cel.width * scale, 196);
      standin.hidden = false;
    }
    if (clean instanceof HTMLCanvasElement) clean.width = clean.height = 0;
    if (!disposed) onReady();
  });
  const transferArt = root.createElement('canvas'); transferArt.id = 'qp-quantum-transfer'; transferArt.hidden = true;
  transferArt.width = 390; transferArt.height = 565; transferArt.setAttribute('aria-hidden', 'true');
  transferArt.style.cssText = 'position:absolute;bottom:0;left:50%;height:100%;width:auto;transform:translateX(-50%);pointer-events:none';
  view.qp.append(transferArt);
  /** @type {HTMLCanvasElement[]} */ const transferCels = [];
  cleanups.push(() => { transferArt.remove(); transferArt.width = transferArt.height = 0; for (const cel of transferCels) cel.width = cel.height = 0; });
  const releasePresence = observeImage(presence, () => {
    if (presence.naturalWidth !== 1254 || presence.naturalHeight !== 1254) return;
    const clean = prepareRunSource(presence), context = standin.getContext('2d');
    if (context && clean instanceof HTMLCanvasElement) {
      // Companion's first greeting pose, with the complete hand and boots.
      // Preserve source resolution; the older reaction cels are only 192px wide.
      standin.width = 512; standin.height = 704;
      context.drawImage(clean, 130, 8, 390, 565, 61, 24, 390, 565);
      standin.dataset.presence = 'ready'; standin.hidden = false;
      for (const cell of [{ x: 710, y: 586 }, { x: 130, y: 8 }]) {
        const cel = root.createElement('canvas'); cel.width = 390; cel.height = 565;
        const c = cel.getContext('2d');
        if (c) { c.drawImage(clean, cell.x, cell.y, 390, 565, 0, 0, 390, 565); transferCels.push(cel); }
      }
    }
    if (clean instanceof HTMLCanvasElement) clean.width = clean.height = 0;
    if (!disposed) onReady();
  });
  cleanups.push(() => { releaseStandin(); releasePresence(); presence.remove(); standin.remove(); standin.width = standin.height = 0; });
  const disabled = portrait(view.resultTitle, 'qp-result-reaction');
  const clearTitle = /** @type {HTMLElement|null} */ (root.querySelector('#clear-name'));
  const celebration = clearTitle ? portrait(clearTitle, 'qp-clear-reaction') : null;
  const release = observeImage(source, () => {
    loaded = source.complete && source.naturalWidth === 1536 && source.naturalHeight === 2288;
    if (!disposed) onReady();
  });
  return {
    /** Use the recent high-resolution calm and greeting sprites during quantum transfer.
     * @param {import('../../game/uplink.js').Transfer|null} transfer
     */
    renderTransfer(transfer) {
      const visible = !!transfer?.quantum && transferCels.length === 2;
      transferArt.hidden = !visible;
      if (visible) {
        const c = transferArt.getContext('2d');
        c?.clearRect(0, 0, 390, 565);
        c?.drawImage(transferCels[transfer?.phase === 'arriving' ? 1 : 0], 0, 0);
      }
      return visible;
    },
    /** @param {import('../../game/types.js').GameEvent} event */
    accept(event) { if (event.type === 'restarted') { idleAge = 0; settling = true; welcomeId++; idleId++; } },
    interrupt() { idleAge = 0; settling = true; idleId++; },
    /** @param {import('../../game/player.js').Position} player @param {number} dt */
    step(player, dt) {
      settling = isStill(player);
      idleAge = settling ? Math.min(IDLE_DELAY, idleAge + dt) : 0;
    },
    /** @param {import('../../game/session.js').Session} session @param {string} flow @param {boolean} paused @param {boolean} reduced @param {boolean} engaged */
    render(session, flow, paused, reduced, engaged) {
      const { player, level } = session;
      view.qp.dataset.idlePaused = String(paused); view.qp.dataset.idleReduced = String(reduced);
      if (engaged) { idleAge = 0; settling = false; }
      const idleVisible = idleAge >= IDLE_DELAY && isStill(player) && !session.transfer && (session.mode === 'free' || level.status === 'playing') && (flow === 'playing' || flow === 'paused');
      welcome.render('welcome', welcomeId, flow === 'ready', paused, reduced);
      disabled.render(level.status === 'won' ? 'celebrate' : 'disabled', `${welcomeId}:${level.status}`, flow === 'ended' && session.mode === 'level', paused, reduced);
      celebration?.render('celebrate', session.result, session.mode === 'level' && !!session.result, paused, reduced);
      return idle.render('idle', idleId, idleVisible, paused, reduced);
    },
    get active() { return loaded && settling && idleAge < IDLE_DELAY; },
    dispose() { disposed = true; release(); releaseSleep(); for (const cleanup of cleanups) cleanup(); source.remove(); sleepSource.remove(); },
  };
}
